<template>
    <div>
        <div class="row">
            <div class="col-md-6 d-md-flex align-items-baseline">
                <h1 class="mr-2">List of Email Defaults</h1>
            </div>
            <div class="col-md-6 pb-3">
                <input-search-icon
                    v-model="search"
                    @search="onSearch"
                    :placeholder="'Search Email Default'"
                    :haveItemOnRightSide="true"
                >
                </input-search-icon>
            </div>
        </div>
        <div class="card card-attribute rounded-lg">
            <div class="card-body overflow-auto p-0">
                <div class="table-holder table-responsive">
                    <table class="table list">
                        <thead>
                            <tr>
                                <th class="width-20">
                                    <sorting
                                        :order-by="
                                            sort.column === 'type'
                                                ? sort.order
                                                : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                onSorting(orderBy, 'type')
                                        "
                                        text="Email:"
                                    >
                                    </sorting>
                                </th>
                                <th class="width-40">
                                    <sorting
                                        :order-by="
                                            sort.column === 'subject'
                                                ? sort.order
                                                : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                onSorting(orderBy, 'subject')
                                        "
                                        text="Subject:"
                                    >
                                    </sorting>
                                </th>
                                <th class="width-20">Body:</th>
                                <th class="width-15">
                                    <sorting
                                        :order-by="
                                            sort.column === 'last_updated'
                                                ? sort.order
                                                : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                onSorting(
                                                    orderBy,
                                                    'last_updated'
                                                )
                                        "
                                        text="Last Updated:"
                                    >
                                    </sorting>
                                </th>
                                <th class="width-5"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-if="isLoading">
                                <tr>
                                    <td colspan="100">
                                        <LoaderList class="w-100" />
                                    </td>
                                </tr>
                            </template>
                            <template v-else>
                                <tr
                                    v-for="email in emailDefaults"
                                    :key="email.id"
                                >
                                    <td>
                                        {{ email.email_type.label }}
                                    </td>
                                    <td v-if="editModeRows[email.id]">
                                        <input
                                            type="text"
                                            v-model="emailSubject[email.id]"
                                            class="form-control"
                                            :class="{
                                                'border-danger': isSubjectError
                                            }"
                                            @change="onChangeSubject"
                                        />
                                    </td>
                                    <td v-else>
                                        {{ email.subject }}
                                    </td>
                                    <td v-if="editModeRows[email.id]">
                                        <v-select
                                            v-model="
                                                selectedEmailTemplate[email.id]
                                            "
                                            :options="emailTemplateList"
                                            name="email-template"
                                            label="label"
                                            :clearable="false"
                                            :reduce="option => option.id"
                                            :menu-class="'select-dropdown'"
                                            :appendToBody="true"
                                        />
                                    </td>
                                    <td v-else>
                                        <template
                                            v-if="_.isEmpty(email.template)"
                                        >
                                            System Default
                                        </template>
                                        <template v-else>
                                            <router-link
                                                :to="{
                                                    name: 'template_details',
                                                    params: {
                                                        type: letterRoute,
                                                        id: email.encoded_id
                                                    }
                                                }"
                                                >{{
                                                    email.template.label
                                                }}</router-link
                                            >
                                        </template>
                                    </td>
                                    <td>
                                        <span v-if="email.update_at">{{
                                            _setDateDisplay(email.update_at)
                                        }}</span>
                                    </td>
                                    <td class="text-right small text-nowrap">
                                        <spinner
                                            v-if="email.isLoading"
                                        ></spinner>
                                        <div v-else>
                                            <span
                                                style="cursor: pointer;"
                                                @click="onEdit(email)"
                                                v-if="
                                                    _hasAccess(
                                                        'setting.email.default.edit'
                                                    ) && !editModeRows[email.id]
                                                "
                                            >
                                                Edit
                                            </span>
                                            <span
                                                v-else-if="
                                                    _hasAccess(
                                                        'setting.email.default.edit'
                                                    ) && editModeRows[email.id]
                                                "
                                            >
                                                <i
                                                    class="fas fa-check mr-3 icon-size"
                                                    v-b-tooltip.hover.top
                                                    title="Save"
                                                    style="cursor: pointer;"
                                                    @click="onSave(email)"
                                                >
                                                </i>
                                                <i
                                                    class="fas fa-times icon-size"
                                                    v-b-tooltip.hover.top
                                                    title="Cancel"
                                                    style="cursor: pointer;"
                                                    @click="onCancelSave"
                                                >
                                                </i>
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-if="!emailDefaults.length">
                                    <td colspan="5">
                                        <i>No records available</i>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>
            <pagination
                :current-page="pagination.page"
                :totalPages="pagination.totalPages"
                :isLoading="isLoading"
                @change-page="page => changePage(page)"
            />
        </div>
    </div>
</template>
<script>
import EmailDefaultService from "@/services/setting/email-default";
import TEMPLATES from "@/constants/templates";
import vSelect from "vue-select";
import { mapValues, keyBy, isEmpty } from "lodash";

export default {
    components: {
        LoaderList: () => import("@/components/elements/loader-list.vue"),
        Sorting: () => import("@/module/commons/sorting.vue"),
        inputSearchIcon: () => import("@/module/commons/input-search-icon.vue"),
        Spinner: () => import("@/components/elements/spinner.vue"),
        Pagination: () => import("@/components/commons/pagination.vue"),
        vSelect
    },
    data() {
        return {
            isLoading: false,
            isSubjectError: false,
            search: "",
            sort: {
                column: "type",
                order: "asc"
            },
            pagination: {
                page: 1,
                totalPages: 1,
                perPage: 50
            },
            emailDefaults: [],
            emailTemplateList: [],
            selectedEmailTemplate: {},
            emailSubject: {},
            editModeRows: {}
        };
    },
    computed: {
        letterRoute() {
            return TEMPLATES.LIST[TEMPLATES.TYPE.LETTER].route;
        }
    },
    mounted() {
        this.getEmailDefaults();
    },
    watch: {},
    methods: {
        onChangeSubject(subjectValue) {
            if (this.isSubjectError && !isEmpty(subjectValue)) {
                this.isSubjectError = false;
            }
        },
        async getEmailDefaults() {
            this.isLoading = true;

            try {
                const res = await EmailDefaultService.getEmailDefaults({
                    search: this.search,
                    page: this.pagination.page,
                    perPage: this.pagination.perPage,
                    orderBy: this.sort.order,
                    order: this.sort.column
                });

                // eslint-disable-next-line camelcase
                const { resources, meta, email_template } = res.data.response;

                if (res.status == 200) {
                    this.emailDefaults = resources;
                    this.pagination.totalPages = meta.lastPage;
                    // eslint-disable-next-line camelcase
                    this.emailTemplateList = email_template;

                    // Initialize selected templates
                    this.assignDefaultEmail();
                }
            } catch (error) {
                console.error(error);
                this._message(
                    "Error: Unable to get the email defaults list.",
                    "danger"
                );
            } finally {
                this.isLoading = false;
            }
        },
        onSorting(orderBy, col) {
            this.sort.order = orderBy;
            this.sort.column = col;
            this.getEmailDefaults();
        },
        onSearch(text) {
            this.search = text;
            this.pagination.page = 1;
            this.pagination.totalPages = 1;
            this.getEmailDefaults();
        },
        onEdit(email) {
            this.assignDefaultEmail();
            this.editModeRows = {};
            this.editModeRows = { ...this.editModeRows, [email.id]: true };
        },
        async onSave(email) {
            const edittedSubject = this.emailSubject?.[email.id];
            if (isEmpty(edittedSubject)) {
                this.isSubjectError = true;
                return;
            }
            try {
                this.editModeRows[email.id] = false;
                const payload = {
                    id: email.id,
                    email_type: email.email_type?.id || null,
                    subject: edittedSubject,
                    template_id: this.selectedEmailTemplate?.[email.id] || null,
                    is_default: email.is_default ? 1 : 0
                };
                this.isLoading = true;
                const res = await EmailDefaultService.saveEmailDefaults(
                    payload
                );

                if (res.status == 200 || res.state == 0) {
                    this._message("Changes saved");
                    this.getEmailDefaults();
                    this.isSubjectError = false;
                } else {
                    throw new Error("Error saving data.");
                }
            } catch (error) {
                console.error(error);
            }
        },
        onCancelSave() {
            this.assignDefaultEmail();
            this.editModeRows = {};
            this.isSubjectError = false;
        },
        changePage(page) {
            this.pagination.page = page;
            this.getEmailDefaults();
        },
        assignDefaultEmail() {
            this.emailSubject = mapValues(
                keyBy(this.emailDefaults, "id"),
                email => email.subject
            );

            this.selectedEmailTemplate = mapValues(
                keyBy(this.emailDefaults, "id"),
                email => email.template?.id || null
            );
        }
    }
};
</script>
<style scoped>
.card-attribute {
    padding: 1rem;
}

.width-20 {
    width: 20%;
}

.width-40 {
    width: 40%;
}

.width-15 {
    width: 15%;
}

.width-5 {
    width: 5%;
}

.icon-size {
    font-size: 1.3em;
}
</style>
